// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@steiner/XDL/Utilities";
import React, { FC, useMemo, useState } from "react";

interface ProductGridCarouselContainerContextType {
    styles: ProductGridCarouselContainerStyle;
}
export interface ProductGridCarouselContainerStyle {
    container?: InjectableCss;
    info?: InjectableCss;
    addToList?: InjectableCss;
    firstContainer?: InjectableCss;
    secondContainer?: InjectableCss;
    price?: InjectableCss;
    availability?: InjectableCss;
    addToCartActions?: InjectableCss;
    image?: InjectableCss;
}

export const productGridCarouselContainerStyle: ProductGridCarouselContainerStyle = {};

const ProductGridCarouselContainerContext = React.createContext<ProductGridCarouselContainerContextType>({
    styles: {},
});

const sharedClassName = "product-grid-container";

const enum NAMES {
    Image = "Image",
    Info = "Info",
    AddToCartActions = "AddToCartActions",
    Price = "Price",
    Availability = "Availability",
    AddToList = "AddToList",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructedChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Info),
        getChild(childrenArray, NAMES.AddToCartActions),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AddToList),
    ];
};

const ProductGridCarouselContainer = (props: {
    children: any;
    extendedStyles?: ProductGridCarouselContainerStyle;
    [key: string]: any;
}) => {
    const { children, extendedStyles, ...otherProps } = props;
    const [styles] = useState(() => mergeWithCss(productGridCarouselContainerStyle, extendedStyles));
    const value = useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [image, info, addToCartActions, price, availability, addToList] = React.useMemo(
        () => getDestructedChildren(childrenArray),
        [childrenArray],
    );
    return (
        <ProductGridCarouselContainerContext.Provider value={value}>
            <StyledWrapper {...styles.container} className={sharedClassName} {...otherProps}>
                {image}
                <StyledWrapper {...styles.firstContainer}>
                    {info}
                    {price}
                    {availability}
                </StyledWrapper>
                <StyledWrapper {...styles.secondContainer}>
                    {addToCartActions}
                    {addToList}
                </StyledWrapper>
            </StyledWrapper>
        </ProductGridCarouselContainerContext.Provider>
    );
};

export const useProductGridCarouselContainerContext = () => {
    const context = React.useContext(ProductGridCarouselContainerContext);
    if (!context) {
        throw new Error(
            "ProductGridTemplate compound components cannot be rendered outside ProductGridCarouselContainer",
        );
    }
    return context;
};

const Image: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({ children, ...otherProps }) => {
    const {
        styles: { image },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...image} className={`${sharedClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};

const Info: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({ children, ...otherProps }) => {
    const {
        styles: { info },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...info} className={`${sharedClassName}-info`}>
            {children}
        </StyledWrapper>
    );
};

const AddToCartActions: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({
    children,
    ...otherProps
}) => {
    const {
        styles: { addToCartActions },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToCartActions} className={`${sharedClassName}-addToCartActions`}>
            {children}
        </StyledWrapper>
    );
};

const AddToList: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({ children, ...otherProps }) => {
    const {
        styles: { addToList },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...addToList} data-test-selector="productGrid-AddToListActions">
            {children}
        </StyledWrapper>
    );
};

const Price: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({ children, ...otherProps }) => {
    const {
        styles: { price },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...price} className={`${sharedClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};

const Availability: FC<React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>> = ({
    children,
    ...otherProps
}) => {
    const {
        styles: { availability },
    } = useProductGridCarouselContainerContext();

    return (
        <StyledWrapper {...otherProps} {...availability} className={`${sharedClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};

ProductGridCarouselContainer.Image = Image;
ProductGridCarouselContainer.Image.displayName = NAMES.Image;
ProductGridCarouselContainer.Info = Info;
ProductGridCarouselContainer.Info.displayName = NAMES.Info;
ProductGridCarouselContainer.AddToCartActions = AddToCartActions;
ProductGridCarouselContainer.AddToCartActions.displayName = NAMES.AddToCartActions;
ProductGridCarouselContainer.Price = Price;
ProductGridCarouselContainer.Price.displayName = NAMES.Price;
ProductGridCarouselContainer.Availability = Availability;
ProductGridCarouselContainer.Availability.displayName = NAMES.Availability;
ProductGridCarouselContainer.AddToList = AddToList;
ProductGridCarouselContainer.AddToList.displayName = NAMES.AddToList;

export default ProductGridCarouselContainer;
